import React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid, Checkbox } from '@material-ui/core'
import { Formik } from 'formik'
import * as Yup from 'yup'

import * as utils from '~/utils'
import { Theme } from '~/styles'
import { Heading, Typography, Input, Button, Header, Main, DateWithValidation } from '~/components'
import { handleChange, handleBlur, handleCheckBox } from '~/utils/formik'
import Loader from '~/components/Loader/Loader'
import theme from '~/styles/theme'
import ErrorIcon from '~/assets/icons/material/error.svg?react'

export interface ProspectNameAndDobCollectionFormValues {
  preferredName: string
  dob: string
  termsAndConditions: boolean
  consentForms?: number[]
}

interface ProspectNameAndDobCollectionFormUIProps {
  initialValues: ProspectNameAndDobCollectionFormValues
  onSubmit: (values: ProspectNameAndDobCollectionFormValues) => void | Promise<void>
  isLoading: boolean
}

/**
 * The name step for `health_plan_prospect` signup: one name field and a date of birth. Its member
 * twin `NameAndDobCollectionFormUI` additionally collects a legal last name and an optional
 * preferred name; a prospect's legal name is collected later, at member conversion, so the name
 * given here is persisted as `preferred_name`.
 *
 * Shared by both prospect hosts: `ProspectNameDobCollection` (unauthenticated) caches the values for
 * `AccountCreation` to post, while `AuthenticatedProspectNameDobCollection` posts them directly.
 */
export const ProspectNameAndDobCollectionFormUI: React.FC<
  ProspectNameAndDobCollectionFormUIProps
> = ({ initialValues, onSubmit, isLoading }) => {
  // `firstNameSchema` because the single name field is labelled "First name" and is required, even
  // though the value persists as `preferred_name`; its messages have to name the visible field.
  const validationSchema = Yup.object().shape({
    preferredName: utils.firstNameSchema,
    dob: utils.dobSchema,
    termsAndConditions: Yup.bool()
      .oneOf([true], 'You need to accept the terms and conditions')
      .required(),
  } as Record<string, any>)

  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Main px={{ xs: 0 }}>
                <Heading>What&apos;s your name?</Heading>
                <Typography variant="subText">
                  We ask for your birthdate to better answer your coverage questions and give
                  provider recommendations.
                </Typography>
                <Box mt={3} />
                <Formik<ProspectNameAndDobCollectionFormValues>
                  validateOnMount={true}
                  validateOnChange={false}
                  initialValues={initialValues}
                  onSubmit={onSubmit}
                  validationSchema={validationSchema}
                >
                  {formik => {
                    return (
                      <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                        <Box my={2}>
                          <Input
                            data-cy="preferredName"
                            label="First name"
                            name="preferredName"
                            required
                            value={formik.values.preferredName || ''}
                            error={
                              formik.touched.preferredName ? formik.errors.preferredName : void 0
                            }
                            autoComplete="given-name"
                            onChange={handleChange(formik, 'preferredName')}
                            onBlur={handleBlur(formik, 'preferredName')}
                            aria-invalid={!!formik.errors.preferredName}
                          />
                        </Box>
                        <Box mb={2}>
                          <DateWithValidation
                            data-cy="dob"
                            values={formik.values}
                            errors={formik.errors}
                            error={formik.touched.dob ? (formik.errors as any).dob : void 0}
                            onChange={handleChange(formik, 'dob')}
                            onBlur={handleBlur(formik, 'dob')}
                          />
                        </Box>
                        <Box mt={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                            mb={3}
                          >
                            <Checkbox
                              data-cy="termsAndConditions"
                              style={{
                                paddingRight: 10,
                                paddingTop: 0,
                                paddingLeft: 0,
                                margin: 0,
                                color: theme.colors.navy[500],
                              }}
                              checked={formik.values.termsAndConditions}
                              onChange={handleCheckBox(formik, 'termsAndConditions')}
                              value={formik.values.termsAndConditions}
                            />
                            <Typography variant={'subText'} color="navy.500">
                              By continuing, I agree to{' '}
                              <a
                                href="/termsAndConditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                css={styles.pressableText}
                              >
                                Firefly Health&apos;s terms and conditions
                              </a>
                            </Typography>
                          </Box>
                          {formik.touched.termsAndConditions && formik.errors.termsAndConditions ? (
                            <Box
                              sx={{
                                mt: -3,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                              }}
                            >
                              <ErrorIcon css={styles.errorIcon} />
                              <Box mt={1}>
                                <Typography css={styles.errorText}>
                                  {`You must agree to our terms and conditions`}
                                </Typography>
                              </Box>
                            </Box>
                          ) : null}
                        </Box>
                        <Box my={5}>
                          <Button
                            data-cy="submit"
                            fullWidth
                            type="submit"
                            css={styles.nextButton}
                            disabled={formik.isSubmitting || isLoading}
                          >
                            Next
                          </Button>
                        </Box>
                      </form>
                    )
                  }}
                </Formik>
              </Main>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  wrapper: () => css({ width: '100%' }),
  errorText: (theme: Theme) =>
    css({
      fontSize: '1.2rem',
      color: theme.colors.red[700],
    }),
  errorIcon: (theme: Theme) =>
    css({
      height: '13.3px',
      width: '13.3px',
      color: theme.colors.red[700],
      display: 'inline',
      marginTop: '4px',
      marginRight: '5px',
      marginBottom: '-2px',
    }),
  nextButton: (theme: Theme) =>
    css({
      marginTop: '25%',
      [theme.mediaQueries.lg]: {
        position: 'relative',
        marginTop: '1%',
      },
    }),
  pressableText: (theme: Theme) =>
    css({
      display: 'inline',
      textDecorationLine: 'underline',
      color: theme.colors.navy['500'],
      cursor: 'pointer',
    }),
}
